import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import { Button, Col, Form, Row, Select } from 'antd';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';

import InvoiceExtraFilters from './InvoiceExtraFilters';
import {
  CompaniesInfiniteSelectField,
  DefaultRouteTabs,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import InvoiceList from '@/components/Fiscal/Invoice/InvoiceList';
import { useDebounce, useDisclosure } from '@/hooks';
import InvoiceCreateModal from '@/components/Fiscal/Invoice/NewInvoiceCreateModal';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { InvoiceListParams } from '@/core/domain/invoice/invoices/invoices.types';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import ExpiredCertificateAlert from './ExpiredCertificateAlert';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import InvoiceDownloadFiles from '@/components/Fiscal/Invoice/InvoiceDownloadFiles';

const InvoicePage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [invoiceCreateModal, invoiceCreateModalAction] = useDisclosure();

  const [form] = Form.useForm();

  const [queryParams, setQueryParams] = useState<InvoiceListParams>({
    invoiceType: InvoiceType.NFE,
    ...convertParams<InvoiceListParams>({
      companyId: 'number',
      invoiceType: 'string',
      serie: 'string',
      number: 'string',
      emissionDate: 'string',
      situation: 'array-string',
      description: 'string',
      entity: 'string',
      entityCode: 'array-string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  useEffect(() => {
    if (queryParams) {
      form.setFieldsValue(queryParams);
    }
  }, []);

  const newRoute = {
    ...route,
    routes: [
      route,
      ...route.routes.filter((route) =>
        ['manifestationRecipient', 'xml'].includes(route.key),
      ),
    ],
  };

  return (
    <DefaultRouteTabs
      route={newRoute}
      persistSearchParams={false}
      permissions={route.permissions}
      extra={
        <Group gap={10}>
          <InvoiceDownloadFiles />

          <Button
            block
            type="primary"
            onClick={invoiceCreateModalAction.open}
            disabled={!hasPermissions([Authorities.FISCAL_INVOICE_ADD])}
          >
            Nova Nota
          </Button>
        </Group>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form}>
        <Row gutter={[12, 12]} align="middle">
          <Col span={24} md={12} lg={6} xxl={6}>
            <FloatingFormItem
              name="companyId"
              label="Empresa"
              style={{ margin: 0 }}
            >
              <CompaniesInfiniteSelectField
                value={queryParams?.companyId}
                queryEnable
                selectFirst={!queryParams?.companyId}
                onChange={(value) =>
                  setQueryParams((prev) => ({ ...prev, companyId: value }))
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={3} xxl={2} style={{ marginRight: 'auto' }}>
            <FloatingFormItem
              name="invoiceType"
              label="Tipo"
              style={{ margin: 0 }}
            >
              <Select
                style={{ width: '100%' }}
                value={queryParams?.invoiceType}
                onChange={(value) =>
                  setQueryParams((prev) => ({
                    ...prev,
                    invoiceType: value,
                  }))
                }
              >
                <Select.Option value={InvoiceType.NFE}>
                  {intl.formatMessage({
                    id: 'invoice.nfse.filters.type.nfe',
                  })}
                </Select.Option>

                <Select.Option value={InvoiceType.NFC}>
                  {intl.formatMessage({
                    id: 'invoice.nfse.filters.type.nfce',
                  })}
                </Select.Option>

                <Select.Option value={InvoiceType.NFS}>
                  {intl.formatMessage({
                    id: 'invoice.nfse.filters.type.nfse',
                  })}
                </Select.Option>
              </Select>
            </FloatingFormItem>
          </Col>

          {queryParams?.companyId && (
            <Col span={24}>
              <ExpiredCertificateAlert companyId={queryParams.companyId} />
            </Col>
          )}
        </Row>
      </Form>

      <AppPageHeader
        extraFilters={
          <InvoiceExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InvoiceList queryParams={queryParams} />

      <InvoiceCreateModal
        open={invoiceCreateModal}
        onCancel={invoiceCreateModalAction.close}
      />
    </DefaultRouteTabs>
  );
};

export default InvoicePage;
